import type { StyleSpecification } from "maplibre-gl";
import { z } from "zod";

import { reportSchemaError } from "@/diagnostics/schema-error.ts";
import type { ResolvedTheme } from "@/theme/theme.ts";

/**
 * The basemap follows the theme rather than being a separate choice: a dark
 * console over a light map (or the reverse) reads as two applications stacked,
 * and which basemap to use was never a decision worth asking for.
 *
 * OpenFreeMap serves key-free vector tiles under a handful of stock styles.
 * Both keys are re-tinted here into this app's palette, so the cartography is
 * part of the design rather than a backdrop the UI happens to sit on.
 */

const STYLE_BASE = "https://tiles.openfreemap.org/styles";

/** Which upstream style each theme starts from, before re-tinting. */
const SOURCE_STYLE: Record<ResolvedTheme, string> = {
  dark: "dark",
  light: "positron",
};

/**
 * Layer id → property overrides. Only properties the layer already declares get
 * touched, so an upstream reshuffle can't inject one that isn't valid there.
 *
 * **The two upstream styles do not share layer ids.** `dark` names its labels
 * `place_other`, `water_name`, `highway_name_*`; positron names the same things
 * `label_other`, `water_name_point_label`, `highway-name-*`. A table written for
 * one and applied to the other doesn't fail — it silently does nothing, which is
 * how the light basemap spent its life wearing stock positron labels while this
 * file appeared to have restyled them. `assertLive` below is the guard.
 */
type Overrides = Record<string, Record<string, unknown>>;

/** Night: deep marine ink, the console's home key. */
const NOCTURNE: Overrides = {
  background: { "background-color": "#04070a" },
  water: { "fill-color": "#061520" },
  waterway: { "line-color": "#061520" },
  landcover_wood: { "fill-color": "#08150f" },
  landuse_park: { "fill-color": "#08150f" },
  landuse_residential: { "fill-color": "#060b0f" },
  building: { "fill-color": "#0b1219", "fill-outline-color": "#111d26" },
  road_area_pier: { "fill-color": "#04070a" },
  road_pier: { "line-color": "#04070a" },
  highway_path: { "line-color": "#101b23" },
  highway_minor: { "line-color": "#0f1a22" },
  highway_major_casing: { "line-color": "#1b2c38" },
  highway_major_inner: { "line-color": "#15222c" },
  highway_major_subtle: { "line-color": "#1b2c38" },
  highway_motorway_casing: { "line-color": "#22394a" },
  highway_motorway_subtle: { "line-color": "#16242f" },
  highway_motorway_inner: { "line-color": "#1e3241" },
  railway_transit: { "line-color": "#152430" },
  railway_minor: { "line-color": "#152430" },
  railway: { "line-color": "#152430" },
  boundary_state: { "line-color": "#243b4a" },
  "boundary_country_z0-4": { "line-color": "#243b4a" },
  "boundary_country_z5-": { "line-color": "#243b4a" },
  water_name: { "text-color": "#3d5866", "text-halo-color": "#04070a" },
  highway_name_other: { "text-color": "#39505d", "text-halo-color": "#04070a" },
  highway_name_motorway: { "text-color": "#445f6d" },
};

/** Day: the same instrument drawn on paper — warm neutrals, cool water. */
const DAY: Overrides = {
  background: { "background-color": "#f3f1ec" },
  water: { "fill-color": "#d4dfe4" },
  waterway: { "line-color": "#d4dfe4" },
  landcover_wood: { "fill-color": "#e2e8dc" },
  park: { "fill-color": "#e4eade" },
  landuse_residential: { "fill-color": "#efece5" },
  building: { "fill-color": "#e7e3da", "fill-outline-color": "#dbd6ca" },
  highway_path: { "line-color": "#e7e3da" },
  highway_minor: { "line-color": "#fdfcfa" },
  highway_major_casing: { "line-color": "#dbd4c7" },
  highway_major_inner: { "line-color": "#fdfcfa" },
  highway_major_subtle: { "line-color": "#e9e5db" },
  highway_motorway_casing: { "line-color": "#d1c9b9" },
  highway_motorway_subtle: { "line-color": "#efebe3" },
  highway_motorway_inner: { "line-color": "#fdfcfa" },
  railway_transit: { "line-color": "#e0dbd0" },
  railway_service: { "line-color": "#e0dbd0" },
  railway: { "line-color": "#e0dbd0" },
  boundary_2: { "line-color": "#c5beaf" },
  boundary_3: { "line-color": "#c5beaf" },
  water_name_point_label: { "text-color": "#4d6472", "text-halo-color": "#f3f1ec" },
  water_name_line_label: { "text-color": "#4d6472", "text-halo-color": "#f3f1ec" },
  waterway_line_label: { "text-color": "#4d6472", "text-halo-color": "#f3f1ec" },
  // These two declare no halo colour upstream, so one can't be added here.
  "highway-name-minor": { "text-color": "#6f6858" },
  "highway-name-major": { "text-color": "#5f5949" },
  "highway-name-path": { "text-color": "#8b8372", "text-halo-color": "#f7f5f1" },
  airport: { "text-color": "#6a6457", "text-halo-color": "#f7f5f1" },
};

/**
 * Neighbourhood and district names, which the two styles disagree about twice
 * over — in what they call the layers and in how legible they leave them.
 *
 * Positron draws them at 9–10px in a letter-spaced uppercase italic, under a 1px
 * white halo with a further 1px of blur. At that weight the halo is wider than
 * the strokes it surrounds, so it eats them from both sides and a nominally
 * near-black label renders as a pale smudge — dark enough in theory, unreadable
 * in practice, and the reason these were fine at night and not by day. Killing
 * the blur and widening the halo puts the glyph back; the size nudge is what
 * makes it comfortable rather than merely possible.
 */
const PLACE_LABELS = {
  dark: [
    "place_other",
    "place_suburb",
    "place_village",
    "place_town",
    "place_city",
    "place_city_large",
    "place_state",
    "place_country_other",
    "place_country_minor",
    "place_country_major",
  ],
  light: [
    "label_other",
    "label_village",
    "label_town",
    "label_state",
    "label_city",
    "label_city_capital",
    "label_country_1",
    "label_country_2",
    "label_country_3",
  ],
} as const;

for (const id of PLACE_LABELS.dark) {
  NOCTURNE[id] = { "text-color": "#6d8794", "text-halo-color": "#04070a" };
}
for (const id of PLACE_LABELS.light) {
  DAY[id] = {
    "text-color": "#3a3730",
    "text-halo-color": "#f7f5f1",
    "text-halo-width": 1.6,
    "text-halo-blur": 0,
  };
}

/** Layout patches, applied on the same terms as the paint ones above. */
const DAY_LAYOUT: Overrides = {};
for (const id of PLACE_LABELS.light) {
  DAY_LAYOUT[id] = { "text-size": ["interpolate", ["linear"], ["zoom"], 8, 10, 12, 11.5] };
}

const OVERRIDES: Record<ResolvedTheme, { paint: Overrides; layout: Overrides }> = {
  dark: { paint: NOCTURNE, layout: {} },
  light: { paint: DAY, layout: DAY_LAYOUT },
};

/**
 * Shallow by design: restating MapLibre's StyleSpecification in zod would be
 * hundreds of fields re-proving what the renderer validates anyway. This only
 * has to catch "that was not a style document" — a captive-portal login page, an
 * error envelope — before `recolor` walks `.layers` and throws on undefined.
 */
const BasemapStyleSchema = z.custom<StyleSpecification>(
  (value) =>
    typeof value === "object" &&
    value !== null &&
    Array.isArray((value as StyleSpecification).layers),
  { message: "expected a MapLibre style document with a layers array" },
);

const cache = new Map<ResolvedTheme, Promise<StyleSpecification>>();

export function loadBasemapStyle(theme: ResolvedTheme): Promise<StyleSpecification> {
  const cached = cache.get(theme);
  if (cached) return cached;

  const promise = fetch(`${STYLE_BASE}/${SOURCE_STYLE[theme]}`)
    .then((res) => {
      if (!res.ok) throw new Error(`basemap ${SOURCE_STYLE[theme]} → HTTP ${res.status}`);
      return res.json();
    })
    .then((raw: unknown) => {
      const parsed = BasemapStyleSchema.safeParse(raw);
      if (!parsed.success) {
        reportSchemaError(`basemap:${SOURCE_STYLE[theme]}`, parsed.error);
        throw new Error(`basemap ${SOURCE_STYLE[theme]} → ${z.prettifyError(parsed.error)}`);
      }
      return recolor(parsed.data, OVERRIDES[theme]);
    });

  // A failed fetch must not poison the cache — the next attempt should retry.
  promise.catch(() => cache.delete(theme));
  cache.set(theme, promise);
  return promise;
}

function recolor(
  style: StyleSpecification,
  { paint, layout }: { paint: Overrides; layout: Overrides },
): StyleSpecification {
  assertLive(style, paint, layout);
  return {
    ...style,
    layers: style.layers.map((layer) => {
      const paintPatch = paint[layer.id];
      const layoutPatch = layout[layer.id];
      if (!paintPatch && !layoutPatch) return layer;
      // Spread, never assign: MapLibre validates `layout: undefined` as a
      // present-but-malformed key and rejects the whole style, so a layer that
      // declares no layout has to come out of here still not declaring one.
      return {
        ...layer,
        ...patched("paint", layer, paintPatch),
        ...patched("layout", layer, layoutPatch),
      } as typeof layer;
    }),
  };
}

/**
 * `{ [slot]: merged }`, or `{}` when there is nothing to merge — and only for
 * keys the layer already declares, per the note on `Overrides`.
 */
function patched(
  slot: "paint" | "layout",
  layer: StyleSpecification["layers"][number],
  patch: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const existing = (slot in layer ? layer[slot as keyof typeof layer] : undefined) as
    | Record<string, unknown>
    | undefined;
  if (!patch || !existing) return {};
  const next = { ...existing };
  for (const [key, value] of Object.entries(patch)) {
    if (key in next) next[key] = value;
  }
  return { [slot]: next };
}

/**
 * Shout about an override that matched nothing.
 *
 * A table keyed by layer id fails silently when upstream renames a layer, and
 * silently is the worst way for a stylesheet to fail: the map still renders, it
 * just quietly wears somebody else's palette. Dev-only — a stale entry is a
 * thing to fix in this file, not a runtime error for a visitor.
 */
function assertLive(style: StyleSpecification, ...tables: Overrides[]): void {
  if (!import.meta.env.DEV) return;
  const present = new Set(style.layers.map((l) => l.id));
  const orphans = tables.flatMap((t) => Object.keys(t)).filter((id) => !present.has(id));
  if (orphans.length > 0) {
    console.warn(
      `[basemap] overrides matched no layer, so they did nothing: ${orphans.join(", ")}`,
    );
  }
}
